import React, { useRef, useState } from "react";
import {
  MdArrowDropDown,
  MdCheckCircleOutline,
  MdOutlineArrowCircleRight,
  MdOutlineCancel,
} from "react-icons/md";
import OptionsPanel from "components/main-view/utils/pop-up-panels/options-pop-up";
import { genderList } from "config/optionValues";
import { AnimatePresence, motion } from "framer-motion";
import { createRecordRequest } from "services/apiRequests";
import { StudentListType } from "utils/interfaces";
import { usePlatform } from "components/utils/PlatformContext";

interface BasicDetailsProps {
  selectedStudent?: StudentListType;
  firstName: string;
  middleName: string;
  lastName: string;
  username: string;
  usernameAvailable:
    | "waiting"
    | "available"
    | "unavailable"
    | "error"
    | "unmodified";
  email: string;
  emailValidation:
    | "waiting"
    | "not-valid"
    | "unavailable"
    | "available"
    | "error"
    | "unmodified";
  dob: string;
  gender: string;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  setMiddleName: React.Dispatch<React.SetStateAction<string>>;
  setLastName: React.Dispatch<React.SetStateAction<string>>;
  setUsername: React.Dispatch<React.SetStateAction<string>>;
  setUsernameAvailable: React.Dispatch<
    React.SetStateAction<
      "waiting" | "available" | "unavailable" | "error" | "unmodified"
    >
  >;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setEmailValidation: React.Dispatch<
    React.SetStateAction<
      | "waiting"
      | "not-valid"
      | "unavailable"
      | "available"
      | "error"
      | "unmodified"
    >
  >;
  setDob: React.Dispatch<React.SetStateAction<string>>;
  setGender: React.Dispatch<React.SetStateAction<string>>;
  setStage: React.Dispatch<React.SetStateAction<number>>;
}

const BasicDetails: React.FC<BasicDetailsProps> = ({
  selectedStudent,
  firstName,
  middleName,
  lastName,
  username,
  usernameAvailable,
  email,
  emailValidation,
  dob,
  gender,
  setFirstName,
  setMiddleName,
  setLastName,
  setUsername,
  setUsernameAvailable,
  setEmail,
  setEmailValidation,
  setDob,
  setGender,
  setStage,
}) => {
  // States definition
  const [showGenderPopUp, setShowGenderPopUp] = useState(false);

  // Get the platform defined values
  const { dataRequirements } = usePlatform();

  /**
   * Condition to activate the next button
   */
  const checkActivateNextStage = () => {
    // Required names condition
    const hasRequiredNames = !!(firstName && lastName);

    // If only username is required, and not email, check the username is available.
    const usernameOnlyScenario =
      dataRequirements.includes("username") &&
      !dataRequirements.includes("email") &&
      (username === "" ||
        ["available", "unmodified"].includes(usernameAvailable));

    // If email is required, then it overwrites the need for a username.
    const emailScenario =
      dataRequirements.includes("email") &&
      email &&
      ["available", "unmodified"].includes(emailValidation);

    return hasRequiredNames && (!!usernameOnlyScenario || !!emailScenario);
  };

  // Variable that defines the
  const nextAvailable = checkActivateNextStage();

  // Reference for the date input
  const dateInputRef = useRef<HTMLInputElement>(null);
  // Ref to access the email input element
  const emailInputRef = useRef<HTMLInputElement>(null);

  const handleClickDate = () => {
    if (dateInputRef.current) dateInputRef.current.showPicker();
  };

  /**
   * Go to the second stage
   */
  const handleNext = () => {
    if (nextAvailable) setStage(2);
  };

  /**
   * Function used to control the behaviour of changing the username
   * @param event
   */
  const handleChangeUsername = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Force the availability state to be waiting
    setUsernameAvailable("waiting");
    // And set the value
    setUsername(event.target.value);
  };

  /**
   * Function used to check if the username exists
   */
  const handleUsernameCheck = async () => {
    // First of all, check if the username is defined
    if (!username) return;

    // Additionally, if we are editing, and the username
    // field is the same as the selected one, then move
    // to the neutral state (waiting)
    if (selectedStudent && selectedStudent.username === username) {
      setUsernameAvailable("unmodified");
      return;
    }

    // Send the check request
    const response = await createRecordRequest(
      { usernameToCheck: username },
      "/api/users/check-username",
    );

    // Check if the response is right
    if (!response.successful) {
      // Display error message
      setUsernameAvailable("error");
      return;
    }

    // Otherwise, display the exist message
    const usernameExists = response.content.exists;
    // Set the state
    setUsernameAvailable(usernameExists ? "unavailable" : "available");
  };

  /**
   * Function used to validate the current email
   */
  const handleEmailCheck = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    // First of all, check if the email is defined
    if (!email) return;

    // Additionally, if we are editing, and the email
    // field is the same as the selected one, then move
    // to the neutral state (waiting)
    if (selectedStudent && selectedStudent.email === email) {
      setEmailValidation("unmodified");
      return;
    }

    // Check if the form is valid using HTML5 checkValidity
    // Reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#validation
    if (!(email && event.target.checkValidity())) {
      // The imail is not valid
      setEmailValidation("not-valid");
      return;
    }

    // Send the check request
    const response = await createRecordRequest(
      { emailToCheck: email },
      "/api/users/check-email",
    );

    // Check if the response is right
    if (!response.successful) {
      // Display error message
      setEmailValidation("error");
      return;
    }

    // Otherwise, display the exist message
    const emailExists = response.content.exists;
    // Set the state
    setEmailValidation(emailExists ? "unavailable" : "available");
  };

  /**
   * Function used to control the behaviour of changing the email
   * @param event
   */
  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Force the availability state to be waiting
    setEmailValidation("waiting");
    // And set the value
    setEmail(event.target.value);
  };

  return (
    <div className="w-[562px] self-stretch p-6 rounded-3xl justify-start items-start flex">
      <div className="w-[514px] flex-col justify-start items-start gap-12 inline-flex">
        <div className="self-stretch h-fit flex-col justify-start items-start flex text-neutral-700 text-[23px] font-semibold font-sans leading-7 select-none">
          Basic details
        </div>
        <div className="self-stretch h-fit flex-col justify-start items-start gap-5 flex">
          {/* {dataRequirements.includes("firstName") && ( */}
          <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
            <label
              htmlFor="first-name"
              className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-sans uppercase leading-[14px] tracking-wide select-none"
            >
              First Name*
            </label>
            <div className="self-stretch  bg-neutral-50 rounded-lg justify-start items-center">
              <input
                id="first-name"
                type="text"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                placeholder="Enter first name"
              />
            </div>
          </div>
          {/* // )} */}
          {dataRequirements.includes("middleName") && (
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <label
                htmlFor="middle-name"
                className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-sans uppercase leading-[14px] tracking-wide select-none"
              >
                Middle name (optional)
              </label>
              <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center">
                <input
                  id="middle-name"
                  type="text"
                  value={middleName}
                  onChange={(event) => setMiddleName(event.target.value)}
                  className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                  placeholder="Enter middle name"
                />
              </div>
            </div>
          )}
          {/* {dataRequirements.includes("lastName") && ( */}
          <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
            <label
              htmlFor="last-name"
              className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-sans uppercase leading-[14px] tracking-wide select-none"
            >
              Last Name*
            </label>
            <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center">
              <input
                id="last-name"
                type="text"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                placeholder="Enter last name"
              />
            </div>
          </div>

          {dataRequirements.includes("username") &&
            !dataRequirements.includes("email") && (
              <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
                <label
                  htmlFor="username"
                  className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-sans uppercase leading-[14px] tracking-wide select-none"
                >
                  Username (optional)
                </label>
                <div
                  className={`self-stretch bg-neutral-50 rounded-lg justify-start items-center border-[1px] ${
                    ["error", "unavailable"].includes(usernameAvailable)
                      ? "border-error-600"
                      : usernameAvailable === "available"
                        ? "border-success-600"
                        : "border-transparent"
                  } relative inline-flex`}
                >
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={handleChangeUsername}
                    onBlur={handleUsernameCheck}
                    className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none z-10"
                    placeholder="Enter username"
                  />
                  <AnimatePresence>
                    {username && usernameAvailable === "available" && (
                      <motion.div
                        key="available"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ease: "easeInOut", duration: 0.2 }}
                        className="absolute right-2 w-6 h-6 flex justify-center items-center"
                      >
                        <MdCheckCircleOutline className="w-full h-full fill-success-500" />
                      </motion.div>
                    )}
                    {username && usernameAvailable === "unavailable" && (
                      <motion.div
                        key="available"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ease: "easeInOut", duration: 0.2 }}
                        className="absolute right-2 w-6 h-6 flex justify-center items-center"
                      >
                        <MdOutlineCancel className="w-full h-full fill-error-500" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="self-stretch h-[14px] inline-flex flex-col justify-start items-start">
                  <AnimatePresence mode="popLayout">
                    {!username && (
                      <motion.div
                        key="empty"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="rounded justify-start items-start gap-2 inline-flex text-neutral-300 text-xs font-sans leading-[14px] tracking-tight select-none"
                      >
                        If not defined, one will be generated automatically
                      </motion.div>
                    )}
                    {username && usernameAvailable === "available" && (
                      <motion.div
                        key="available"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="rounded justify-start items-start gap-2 inline-flex text-success-500 text-xs font-sans leading-[14px] tracking-tight select-none"
                      >
                        The provided username is available
                      </motion.div>
                    )}
                    {username && usernameAvailable === "unavailable" && (
                      <motion.div
                        key="unavailable"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="rounded justify-start items-start gap-2 inline-flex text-error-500 text-xs font-sans leading-[14px] tracking-tight select-none"
                      >
                        The provided username is not available
                      </motion.div>
                    )}
                    {username && usernameAvailable === "error" && (
                      <motion.div
                        key="error"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="rounded justify-start items-start gap-2 inline-flex text-error-500 text-xs font-sans leading-[14px] tracking-tight select-none"
                      >
                        Error when trying to check the username
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

          {dataRequirements.includes("email") && (
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <label
                htmlFor="email"
                className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none"
              >
                Email*
              </label>
              <div className="self-stretch h-fit inline-flex flex-col justify-start items-start">
                <div
                  className={`self-stretch bg-neutral-50 border ${
                    emailValidation === "waiting"
                      ? "border-transparent"
                      : ["not-valid", "unavailable"].includes(emailValidation)
                        ? "border-error-500"
                        : emailValidation === "available"
                          ? "border-success-500"
                          : "border-transparent"
                  } rounded-lg justify-start items-center gap-2 inline-flex relative`}
                >
                  <input
                    ref={emailInputRef}
                    id="email"
                    type="email"
                    value={email}
                    onBlur={handleEmailCheck}
                    onChange={handleChangeEmail}
                    className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none z-10"
                    placeholder="Enter email"
                  />
                  <AnimatePresence>
                    {emailValidation === "available" && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ease: "easeInOut", duration: 0.2 }}
                        className="absolute right-2 w-6 h-6 flex justify-center items-center"
                      >
                        <MdCheckCircleOutline className="w-full h-full fill-success-500" />
                      </motion.div>
                    )}
                    {["not-valid", "unavailable"].includes(emailValidation) && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ ease: "easeInOut", duration: 0.2 }}
                        className="absolute right-2 w-6 h-6 flex justify-center items-center"
                      >
                        <MdOutlineCancel className="w-full h-full fill-error-500" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <AnimatePresence mode="wait" initial={false}>
                  {emailValidation === "not-valid" && (
                    <motion.div
                      key="not-valid"
                      className="w-full text-error-600 text-xs font-normal font-sans tracking-tight leading-[17px] select-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      The email format is incorrect
                    </motion.div>
                  )}
                  {emailValidation === "error" && (
                    <motion.div
                      key="error"
                      className="w-full text-error-600 text-xs font-normal font-sans tracking-tight leading-[17px] select-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      Error when trying to check the email
                    </motion.div>
                  )}
                  {emailValidation === "unavailable" && (
                    <motion.div
                      key="unavailable"
                      className="w-full text-error-600 text-xs font-normal font-sans tracking-tight leading-[17px] select-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      The provided email is already taken
                    </motion.div>
                  )}
                  {emailValidation === "available" && (
                    <motion.div
                      key="available"
                      className="w-full text-success-600 text-xs font-normal font-sans tracking-tight leading-[17px] select-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      The provided email is available
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
        <div className="w-full h-fit flex-col justify-start items-start gap-5 flex">
          {dataRequirements.includes("dob") && (
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <label
                htmlFor="date-of-birth"
                className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-sans uppercase leading-[14px] tracking-wide select-none"
              >
                Date of Birth
              </label>
              <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center">
                <input
                  id="date-of-birth"
                  ref={dateInputRef}
                  type="date"
                  value={dob}
                  onChange={(event) => setDob(event.target.value)}
                  onClick={handleClickDate}
                  className={
                    dob
                      ? "w-full h-full p-3 text-neutral-700 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none cursor-pointer"
                      : "w-full h-full p-3 text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none cursor-pointer"
                  }
                />
              </div>
            </div>
          )}
          {dataRequirements.includes("gender") && (
            <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
              <div className="rounded justify-start items-start gap-2 inline-flex text-neutral-500 text-xs font-sans uppercase leading-[14px] tracking-wide select-none">
                Gender
              </div>
              <button
                className="self-stretch p-3 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-lg justify-start text-start items-center gap-2 inline-flex cursor-pointer"
                onClick={() => setShowGenderPopUp(true)}
              >
                <div className="grow shrink basis-0 h-[27px] py-1 justify-start items-start gap-2 flex">
                  <div
                    className={
                      gender
                        ? "grow shrink basis-0 h-[19px] text-neutral-700 text-base font-normal font-sans leading-[19px]"
                        : "grow shrink basis-0 h-[19px] text-neutral-300 text-base font-normal font-sans leading-[19px]"
                    }
                  >
                    {gender ? gender : "Select an option"}
                  </div>
                </div>
                <MdArrowDropDown className="w-5 h-5 fill-neutral-600" />
              </button>
            </div>
          )}
        </div>
        <div className="w-full h-[46px] self-stretch justify-end items-center inline-flex">
          <button
            className={`group rounded justify-center items-center gap-2 flex ${
              nextAvailable ? "cursor-pointer" : "cursor-not-allowed"
            }`}
            onClick={handleNext}
          >
            <div
              className={
                nextAvailable
                  ? "text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug"
                  : "text-neutral-200 text-lg font-semibold font-sans leading-snug"
              }
            >
              Next
            </div>
            <MdOutlineArrowCircleRight
              className={
                nextAvailable
                  ? "w-6 h-6 fill-secondary-600 group-hover:fill-secondary-700"
                  : "w-6 h-6 fill-neutral-200"
              }
            />
          </button>
        </div>
      </div>
      {/* Pop up panels */}
      <AnimatePresence>
        {/* Gender pop-up */}
        {showGenderPopUp && (
          <OptionsPanel
            options={genderList}
            setShowPopUp={setShowGenderPopUp}
            setOptions={setGender}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BasicDetails;
